<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  rot: number; vr: number;
  color: string; size: number; life: number;
}

const COLORS = ['#1FA8F2', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6', '#EC4899', '#0E7DBE'];

const canvasEl = ref<HTMLCanvasElement | null>(null);
let particles: Particle[] = [];
let raf = 0;

function resize() {
  const c = canvasEl.value;
  if (!c) return;
  const dpr = window.devicePixelRatio || 1;
  c.width  = window.innerWidth  * dpr;
  c.height = window.innerHeight * dpr;
  c.style.width  = window.innerWidth  + 'px';
  c.style.height = window.innerHeight + 'px';
  const ctx = c.getContext('2d');
  ctx?.scale(dpr, dpr);
}

function spawnBurst(originX: number, originY: number, count: number) {
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI - Math.PI / 2;     // upward fan
    const speed = 6 + Math.random() * 8;
    particles.push({
      x: originX,
      y: originY,
      vx: Math.cos(angle) * speed * (Math.random() < 0.5 ? -1 : 1),
      vy: Math.sin(angle) * speed - 4,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.35,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      size: 5 + Math.random() * 7,
      life: 1,
    });
  }
}

function tick() {
  const c = canvasEl.value;
  if (!c) return;
  const ctx = c.getContext('2d');
  if (!ctx) return;

  const w = window.innerWidth;
  const h = window.innerHeight;
  ctx.clearRect(0, 0, w, h);

  particles = particles.filter(p => p.life > 0 && p.y < h + 40);
  for (const p of particles) {
    p.vy += 0.35;        // gravity
    p.vx *= 0.995;       // slight air drag
    p.x  += p.vx;
    p.y  += p.vy;
    p.rot += p.vr;
    p.life -= 0.006;

    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillStyle  = p.color;
    ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
    ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size * 0.5);
    ctx.restore();
  }

  if (particles.length > 0) {
    raf = requestAnimationFrame(tick);
  } else {
    raf = 0;
  }
}

function fire() {
  resize();
  const w = window.innerWidth;
  const h = window.innerHeight;
  spawnBurst(w * 0.25, h * 0.5, 80);
  spawnBurst(w * 0.75, h * 0.5, 80);
  spawnBurst(w * 0.5,  h * 0.4, 60);
  if (!raf) raf = requestAnimationFrame(tick);

  // Second wave for a longer celebration.
  setTimeout(() => spawnBurst(w * 0.5, h * 0.45, 80), 350);
  setTimeout(() => {
    spawnBurst(w * 0.2, h * 0.6, 50);
    spawnBurst(w * 0.8, h * 0.6, 50);
  }, 700);
}

defineExpose({ fire });

onMounted(() => {
  resize();
  fire();
  window.addEventListener('resize', resize);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', resize);
  cancelAnimationFrame(raf);
  particles = [];
});
</script>

<template>
  <canvas ref="canvasEl" class="confetti-canvas" aria-hidden="true" />
</template>

<style scoped>
.confetti-canvas {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 99999;
}
</style>
